import React, { useState, useEffect } from 'react';
import { X, DollarSign, Save } from 'lucide-react';

const FloorEditModal = ({ isOpen, onClose, onSave, floors = {}, currentSite = '', availablePlacements = [] }) => {
  const [floorData, setFloorData] = useState({
    version: '2.0.0',
    site: '',
    enabled: true,
    currency: 'EUR',
    default: 0.25,
    overrides: {},
    bidderFloors: {},
    rules: {
      skipRate: 0.1,
      modelVersion: 'v2.0',
      enforcement: 'soft'
    },
    lastUpdated: new Date().toISOString().split('T')[0],
    notes: {
      structure: 'Simplified floor pricing using placement names directly',
      default: 'All placements use default floor unless overridden'
    }
  });

  // Initialize form data when modal opens
  useEffect(() => {
    if (isOpen && floors) {
      setFloorData({
        version: floors.version || '2.0.0',
        site: floors.site || currentSite,
        enabled: floors.enabled ?? true,
        currency: floors.currency || 'EUR',
        default: floors.default || 0.25,
        overrides: floors.overrides || {},
        bidderFloors: floors.bidderFloors || {},
        rules: {
          skipRate: floors.rules?.skipRate ?? 0.1,
          modelVersion: floors.rules?.modelVersion || 'v2.0',
          enforcement: floors.rules?.enforcement || 'soft'
        },
        lastUpdated: floors.lastUpdated || new Date().toISOString().split('T')[0],
        notes: floors.notes || {
          structure: 'Simplified floor pricing using placement names directly',
          default: 'All placements use default floor unless overridden'
        }
      });
    }
  }, [isOpen, floors, currentSite]);

  const handleSave = () => {
    // Clean up floor data before saving
    const cleanedFloorData = {
      ...floorData,
      lastUpdated: new Date().toISOString().split('T')[0],
      site: currentSite || floorData.site || 'unknown',
      // Ensure default is a number
      default: typeof floorData.default === 'string' && floorData.default === '' 
        ? 0 
        : parseFloat(floorData.default) || 0
    };
    
    // Clean up override values
    if (cleanedFloorData.overrides) {
      Object.keys(cleanedFloorData.overrides).forEach(placement => {
        const override = cleanedFloorData.overrides[placement];
        if (override.desktop === '') {
          override.desktop = cleanedFloorData.default;
        } else {
          override.desktop = parseFloat(override.desktop) || 0;
        }
        if (override.mobile === '') {
          override.mobile = cleanedFloorData.default;
        } else {
          override.mobile = parseFloat(override.mobile) || 0;
        }
      });
    }
    
    // Ensure skipRate is a number
    if (cleanedFloorData.rules && cleanedFloorData.rules.skipRate !== undefined) {
      cleanedFloorData.rules.skipRate = parseFloat(cleanedFloorData.rules.skipRate) || 0;
    }
    
    onSave(cleanedFloorData);
    onClose();
  };

  const updateDefaultFloor = (value) => {
    setFloorData(prev => ({
      ...prev,
      default: value === '' ? '' : isNaN(parseFloat(value)) ? 0 : parseFloat(value)
    }));
  };

  const updatePlacementFloor = (placement, device, value) => {
    setFloorData(prev => ({
      ...prev,
      overrides: {
        ...prev.overrides,
        [placement]: {
          ...prev.overrides[placement],
          [device]: value === '' ? '' : isNaN(parseFloat(value)) ? 0 : parseFloat(value)
        }
      }
    }));
  };

  const addPlacementOverride = (placementName) => {
    setFloorData(prev => ({
      ...prev,
      overrides: {
        ...prev.overrides,
        [placementName]: {
          desktop: prev.default,
          mobile: prev.default
        }
      }
    }));
  };

  const removePlacementOverride = (placementName) => {
    setFloorData(prev => {
      const newOverrides = { ...prev.overrides };
      delete newOverrides[placementName];
      return {
        ...prev,
        overrides: newOverrides
      };
    });
  };

  if (!isOpen) return null;

  // Get placement names from current overrides
  const placementNames = Object.keys(floorData.overrides);
  
  // Get available placements that don't have overrides yet
  const availableForOverride = availablePlacements.filter(p => !placementNames.includes(p));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Edit Floor Prices</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={24} />
          </button>
        </div>

        {/* Global Settings */}
        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Global Settings</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Floor Enabled
              </label>
              <select
                value={floorData.enabled}
                onChange={(e) => setFloorData(prev => ({ ...prev, enabled: e.target.value === 'true' }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="true">Enabled</option>
                <option value="false">Disabled</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Currency
              </label>
              <select
                value={floorData.currency}
                onChange={(e) => setFloorData(prev => ({ ...prev, currency: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="EUR">EUR</option>
                <option value="USD">USD</option>
                <option value="GBP">GBP</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Default Floor ({floorData.currency})
              </label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={floorData.default}
                  onChange={(e) => updateDefaultFloor(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="0.25"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Placement Floor Overrides */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Placement Floor Overrides</h3>
            {availableForOverride.length > 0 && (
              <select
                onChange={(e) => {
                  if (e.target.value) {
                    addPlacementOverride(e.target.value);
                    e.target.value = '';
                  }
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Add Placement Override...</option>
                {availableForOverride.map(placement => (
                  <option key={placement} value={placement}>
                    {placement}
                  </option>
                ))}
              </select>
            )}
          </div>
          
          {placementNames.length > 0 ? (
            <div className="space-y-4">
              {placementNames.map(placement => (
                <div key={placement} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="font-medium text-gray-900 capitalize">{placement}</h4>
                    <button
                      onClick={() => removePlacementOverride(placement)}
                      className="text-red-500 hover:text-red-700 text-sm"
                    >
                      Remove
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Desktop ({floorData.currency})
                      </label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={floorData.overrides[placement]?.desktop || ''}
                          onChange={(e) => updatePlacementFloor(placement, 'desktop', e.target.value)}
                          className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder={floorData.default}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Mobile ({floorData.currency})
                      </label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={floorData.overrides[placement]?.mobile || ''}
                          onChange={(e) => updatePlacementFloor(placement, 'mobile', e.target.value)}
                          className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder={floorData.default}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              <p>No placement overrides defined.</p>
              <p className="text-sm mt-1">Floors will use the default value for all placements.</p>
              {availableForOverride.length > 0 && (
                <p className="text-sm mt-2">Use the dropdown above to add placement-specific floors.</p>
              )}
            </div>
          )}
        </div>

        {/* Floor Rules */}
        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Floor Rules</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Enforcement
              </label>
              <select
                value={floorData.rules.enforcement}
                onChange={(e) => setFloorData(prev => ({
                  ...prev,
                  rules: { ...prev.rules, enforcement: e.target.value }
                }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="soft">Soft (Warning only)</option>
                <option value="hard">Hard (Reject bids)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Skip Rate
              </label>
              <input
                type="number"
                step="0.1"
                min="0"
                max="1"
                value={floorData.rules.skipRate}
                onChange={(e) => setFloorData(prev => ({
                  ...prev,
                  rules: { ...prev.rules, skipRate: parseFloat(e.target.value) || 0 }
                }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="0.1"
              />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Save size={16} />
            Save Floors
          </button>
        </div>
      </div>
    </div>
  );
};

export default FloorEditModal;